import React, { useEffect, useState } from "react";
import { IoIosArrowForward, IoIosArrowBack } from "react-icons/io";
import { CiNoWaitingSign } from "react-icons/ci";
import { FaStar } from "react-icons/fa";
import { useAuth } from "context/auth-context";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import completeIcon from "../../assets/completeSurvey/success.png";
import smileEmoji from "../../assets/completeSurvey/completeEmoji.svg";
import { getsurveysPointApi, insertOtpForExistEmailApi, updateEmailOfPanelistApi, updatePasswordForExistEmailApi, verifyIsOtpAreValidateApi } from "./surveycomplete_api";
import { Link } from "react-router-dom";
import { toastrSuccess } from "./../../lib/toastr";

function SuccessPopup() {
  const { setAuth } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({ rating: 0 });
  const [surveyPoints, setSurveyPoints] = useState<number>(0);
  const [sessionId, setSessionId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [otp, setOtp] = useState<string>("");
  const [uid, setUid] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestUser, setIsGuestUser] = useState(0);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");

  const validateEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateOtp = (otp: string) => {
    return /^\d{6}$/.test(otp);
  };

  const fetchPoints = async (sessionId: string, rating: number, uid: string) => {
    const response = await getsurveysPointApi(sessionId, rating, uid);
    if (response.success === true) {
      setSurveyPoints(response.data);
      setIsGuestUser(response?.isGuestUser);
    } else {
      setSurveyPoints(0);
      setIsGuestUser(0);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const uidParam = params.get("UID");
    const sessionIdParam = params.get("SESSION_ID");

    if (uidParam) setUid(uidParam);
    if (sessionIdParam) setSessionId(sessionIdParam);
    if (sessionIdParam) fetchPoints(sessionIdParam, data.rating, uidParam);
  }, []);

  const handleSurveyCheck = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // Validate OTP input
    if (!validateOtp(otp)) {
      setOtpError("OTP must be exactly 6 digits.");
      return;
    }
    setOtpError("");

    // Ensure email & OTP exist before proceeding
    if (!email || !otp) {
      setOtpError("Email and OTP are required.");
      return;
    }

    try {
      // Validate OTP via API
      const response = await verifyIsOtpAreValidateApi(email, otp);

      if (!response?.success) {
        setOtpError(response?.message || "OTP verification failed.");
        setIsErrorModalOpen(true);
        return;
      }

      // If uid or sessionId is missing, redirect to surveys
      if (!uid || !sessionId) {
        navigate("/my-surveys");
        return;
      }

      setIsLoading(true);
      
      // Update email of panelist
      const updateEmailResponse = await updateEmailOfPanelistApi(uid, email, sessionId);
      if (!updateEmailResponse?.success) {
        setErrorMessage(updateEmailResponse?.message || "Failed to update email.");
        setIsErrorModalOpen(true);
        return;
      }

      // Update password for the existing email
      const updateResponse = await updatePasswordForExistEmailApi(email);
      
      if (updateResponse?.success) {
        toastrSuccess(t("form.login.successful"));
        setAuth(updateResponse?.response.result);
        navigate(location.state || "/dashboard", { state: null });
      } else {
        navigate("/my-surveys");
      }

    } catch (error) {
      setOtpError("An unexpected error occurred. Please try again.");
      setIsErrorModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };


  const handleArrowClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setIsLoading(true);
      const response = await insertOtpForExistEmailApi(email);
      setIsLoading(false);

      if (response?.success === false) {
        setErrorMessage(response?.message);
      } else {
        toastrSuccess("Otp Sent on your Email-ID");
        setEmailError("");
        setShowOtpInput(true);
      }
    }
  };

  return (
    <div className="fixed top-0 right-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50 sm:left-0 font-poppins">
      <div className={`relative left-2 sm:left-0 w-full max-w-md ${isGuestUser === 1 ? "py-6 h-[95vh] sm:h-[65vh] lg:h-[90vh] 2xl:h-fit overflow-y-auto" : "py-12 h-fit"} px-6 sm:px-14 text-center bg-white shadow-lg rounded-3xl md:max-w-lg text-[#434343]`}>
        <img src={completeIcon} alt="Success" className="w-24 mx-auto" />
        <h2 className={`${isGuestUser === 1 ? "mt-2" : "mt-5 mb-6"} flex items-center justify-center gap-2 text-2xl font-medium text-[#00A411]`}>
          Congratulations! <img src={smileEmoji} alt="emoji" className="w-6" />
        </h2>
        <p className="mt-2 text-sm text-[#434343]">Though you&apos;re moving on, our digital door will always be open for you.</p>
        <p className="mt-4 text-base text-[#434343]">Wishing you success and happiness ahead!</p>

        {/* Reward Section */}
        <div className={`${isGuestUser === 1 ? "mt-4" : "mt-8"} text-base font-medium text-[#056E9C]`}>
          Your Reward
        </div>
        <div className={`${isGuestUser === 1 ? "mt-1" : "mt-2 mb-8"} text-2xl font-medium text-[#056E9C]`}>{surveyPoints} Points</div>

        {/* Email & OTP Section */}
        {isGuestUser === 1 && (
          <div className="mt-4 text-left">
            <label className="block text-sm lg:text-base text-[#434343]">{showOtpInput ? "Enter OTP to Confirm" : "Enter Your Email"}</label>
            <div className="relative flex items-center">
              {showOtpInput ? (
                <div className="w-full">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    setOtpError("");
                    setErrorMessage("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault(); 
                    }
                  }}
                  placeholder="Enter OTP"
                  className="w-full p-3 mt-1 border rounded-lg focus:outline-[#056E9C]"
                />
                 { isGuestUser && email !== '' && otp !== '' ?
                 <div>
                 <button
                    onClick={handleSurveyCheck}
                    className="absolute text-xl text-blue-500 cursor-pointer right-3 top-5"
                    disabled={isLoading}
                  >
                    {isLoading ? <CiNoWaitingSign /> : <IoIosArrowForward />}
                  </button> 
                  <p className="text-sm text-red-500">{otpError}</p>
                 </div>: null }
                <div className="flex justify-between mt-2">
                  <button
                    onClick={() => {
                      setShowOtpInput(false);
                      setOtp("");
                      setOtpError("");
                    }}
                    className="text-xs text-[#056E9C] cursor-pointer flex items-center gap-1"
                  >
                    <IoIosArrowBack /> Back to Email
                  </button>
                  <button
                    onClick={handleArrowClick}
                    className="text-end text-xs text-[#056E9C] cursor-pointer"
                  >
                    Resend OTP
                  </button>
                </div>
              </div>
              ) : (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                      setErrorMessage("");
                    }}
                    placeholder="Enter your email"
                    className="w-full p-3 mt-1 border rounded-lg focus:outline-[#056E9C]"
                  />
                  <button
                    onClick={handleArrowClick}
                    className="absolute text-xl text-blue-500 cursor-pointer right-3 top-5"
                    disabled={isLoading}
                  >
                    {isLoading ? <CiNoWaitingSign /> : <IoIosArrowForward />}
                  </button>
                </>
              )}
            </div>

            {emailError && <p className="mt-1 text-xs text-red-500">{emailError}</p>}
            <p className="mt-1 text-xs">* Please note, if you do not complete this process, you will not receive any earnings.</p>
          </div>
        )}

        {/* Rating Section */}
        <div className="mt-4">
          <p className="text-sm">Please rate your experience</p>
          <div className="flex justify-center gap-2 mt-2">
            {[1, 2, 3, 4, 5].map((num) => (
              <FaStar
                key={num}
                className={`text-2xl cursor-pointer transition-transform transform ${data.rating >= num ? "text-yellow-400 scale-110" : "text-gray-300 hover:scale-110"}`}
                onClick={() => setData({ ...data, rating: num })}
              />
            ))}
          </div>
        </div>

        {isGuestUser === 0 && <Link to="/my-surveys">
          <button className="w-52 h-8 lg:w-72 lg:h-14 md:w-64 md:h-12 bg-[#056E9C] lg:text-base md:text-sm text-xs text-white rounded-3xl lg:mt-8 md:mt-6 mt-4">
            My Surveys
          </button>
        </Link>}

        {isErrorModalOpen && (
          <div className="fixed top-0 left-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-60">
            <div className="p-6 text-center bg-white rounded-lg shadow-lg">
              <h2 className="text-lg font-semibold text-red-600">Error</h2>
             <p className="mt-2 text-gray-600">
                {errorMessage && <span>{errorMessage}</span>}
                {emailError && <span> {emailError}</span>}
                {otpError && <span> {otpError}</span>}
              </p>
              <button
                onClick={() => navigate("/my-surveys")}
                className="px-4 py-2 mt-4 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SuccessPopup;
